import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Restore as RestoreIcon,
  CloudQueue as CloudIcon,
  List as ListIcon,
} from '@mui/icons-material';
import {
  Box,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { PageLayout } from '../components/ui/PageLayout';
import {
  AdminTabs,
  type AdminTab,
} from '../components/admin';
import { EmbeddingConfigEditDialog } from '../components/EmbeddingConfig';
import { useSnackbar } from '../contexts/SnackbarContext';

import { EmbeddingTestResponse } from '../api/ApiClient';
import { 
  useGetEmbeddingConfigs, 
  useGetCurrentEmbeddingConfig, 
  useGetEmbeddingProviders,
  useSaveEmbeddingConfig,
  useTestEmbeddingConfig,
  useDeleteEmbeddingConfig,
  useActivateEmbeddingConfig,
  useResetToEnvEmbeddingConfig
} from '@/hooks/api/config/useEmbeddingConfig';
import { log } from '../utils/errors/logger';

interface EmbeddingProvider {
  value: string;
  label: string;
  description: string;
  default_model: string;
  default_vector_size: number;
  required_config: string[];
  optional_config: string[];
  is_active: boolean;
}

interface EmbeddingConfig {
  id?: string;
  provider: string;
  model: string;
  vector_size: number;
  batch_size: number;
  timeout: number;
  max_retries: number;
  provider_config: Record<string, any>;
  display_name: string;
  description: string;
  is_active: boolean;
  created_by?: string;
  updated_by?: string;
}

export default function EmbeddingConfig() {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  
  // TanStack Query hooks
  const { data: providers = [], isLoading: providersLoading } = useGetEmbeddingProviders();
  const { data: configs = [], isLoading: configsLoading } = useGetEmbeddingConfigs();
  const { data: currentConfig, isLoading: currentConfigLoading } = useGetCurrentEmbeddingConfig();
  
  // Mutation hooks
  const saveConfigMutation = useSaveEmbeddingConfig();
  const testConfigMutation = useTestEmbeddingConfig();
  const deleteConfigMutation = useDeleteEmbeddingConfig();
  const activateConfigMutation = useActivateEmbeddingConfig();
  const resetToEnvMutation = useResetToEnvEmbeddingConfig();
  
  // Derived loading state (exclude activate mutation as it has its own dialog loading)
  const loading = providersLoading || configsLoading || currentConfigLoading || 
                 saveConfigMutation.isPending || testConfigMutation.isPending || 
                 deleteConfigMutation.isPending || resetToEnvMutation.isPending;
  
  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<Partial<EmbeddingConfig> | null>(null);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);
  const [configToActivate, setConfigToActivate] = useState<EmbeddingConfig | null>(null);
  const [testResult, setTestResult] = useState<EmbeddingTestResponse | null>(null);

  const handleTestConfig = (config: Partial<EmbeddingConfig>) => {
    log.user.action('embedding_config_test_initiated', 'EmbeddingConfig', { model: config.model });
    testConfigMutation.mutate(config, {
      onSuccess: (result) => {
        setTestResult(result);
        setTestDialogOpen(true);
        log.user.action('embedding_config_test_completed', 'EmbeddingConfig', {
          success: result.success,
          model: config.model
        });
      },
      onError: () => {
        showError(t('embedding.messages.testFailed'));
      }
    });
  };

  const handleSaveConfig = (config: Partial<EmbeddingConfig>) => {
    log.user.action('embedding_config_save_initiated', 'EmbeddingConfig', { model: config.model });
    saveConfigMutation.mutate(config, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.saveSuccess'));
        setConfigDialogOpen(false);
        log.user.action('embedding_config_saved', 'EmbeddingConfig', { model: config.model });
      },
      onError: () => {
        showError(t('embedding.messages.saveFailed'));
      }
    });
  };

  const handleDeleteConfig = (configId: string) => {
    log.user.action('embedding_config_delete_initiated', 'EmbeddingConfig', { configId });
    deleteConfigMutation.mutate(configId, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.deleteSuccess'));
        setDeleteDialogOpen(false);
        log.user.action('embedding_config_deleted', 'EmbeddingConfig', { configId });
      },
      onError: () => {
        showError(t('embedding.messages.deleteFailed'));
      }
    });
  };

  const handleActivateConfig = (configId: string) => {
    log.user.action('embedding_config_activate_initiated', 'EmbeddingConfig', { configId });

    // Close dialog immediately for better UX
    setActivateDialogOpen(false);
    setConfigToActivate(null);

    activateConfigMutation.mutate(configId, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.activateSuccess'));
        log.user.action('embedding_config_activated', 'EmbeddingConfig', { configId });
      },
      onError: (error) => {
        log.api.error('POST', '/api/admin/embedding-config/activate', error);
        showError(t('embedding.messages.activateFailed'));
      }
    });
  };

  const handleResetToEnv = () => {
    log.user.action('embedding_config_reset_to_env_initiated', 'EmbeddingConfig');
    resetToEnvMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.resetSuccess'));
        log.user.action('embedding_config_reset_to_env_completed', 'EmbeddingConfig');
      },
      onError: () => {
        showError(t('embedding.messages.resetFailed'));
      }
    });
  };

  const openEditDialog = (config?: EmbeddingConfig) => {
    if (config) {
      setEditingConfig({ ...config });
    } else {
      const defaultProvider = providers[0];
      setEditingConfig({
        provider: defaultProvider?.value || 'openai',
        model: defaultProvider?.default_model || 'text-embedding-ada-002',
        vector_size: defaultProvider?.default_vector_size || 1536,
        batch_size: 100,
        timeout: 30,
        max_retries: 3,
        provider_config: {},
        display_name: '',
        description: '',
        is_active: false,
      });
    }
    setConfigDialogOpen(true);
  };

  // Define tabs with standardized structure
  const tabs: AdminTab[] = [
    {
      label: t('embedding.tabs.currentConfiguration'),
      icon: <SettingsIcon />,
      content: (
        <>
          {currentConfig && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {currentConfig.display_name || t('embedding.tabs.currentConfiguration')}
                  </Typography>
                  <Chip 
                    label={t('embedding.currentConfig.active')} 
                    color="success" 
                    icon={<CheckIcon />}
                    size="small"
                  />
                </Box>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('embedding.currentConfig.provider')}</Typography>
                    <Typography variant="body1">{currentConfig.provider}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('embedding.currentConfig.model')}</Typography>
                    <Typography variant="body1">{currentConfig.model}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('embedding.currentConfig.vectorSize')}</Typography>
                    <Typography variant="body1">{currentConfig.vector_size}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('embedding.currentConfig.batchSize')}</Typography>
                    <Typography variant="body1">{currentConfig.batch_size}</Typography>
                  </Grid>
                </Grid>
                
                {currentConfig.description && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('embedding.currentConfig.description')}</Typography>
                    <Typography variant="body1">{currentConfig.description}</Typography>
                  </Box>
                )}
              </CardContent>
              <CardActions>
                {/* <Button
                  startIcon={<EditIcon />}
                  onClick={() => openEditDialog(currentConfig)}
                >
                  {t('embedding.actions.edit')}
                </Button> */}
                {/* <Button
                  startIcon={<TestIcon />}
                  onClick={() => handleTestConfig(currentConfig)}
                >
                  {t('embedding.actions.test')}
                </Button> */}
                {/* <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleResetToEnv}
                  color="warning"
                >
                  {t('embedding.actions.resetToEnvironment')}
                </Button> */}
              </CardActions>
            </Card>
          )}
        </>
      ),
    },
    {
      label: t('embedding.tabs.allConfigurations'),
      icon: <ListIcon />,
      content: (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{t('embedding.tabs.allConfigurations')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => openEditDialog()}
              >
                {t('embedding.actions.addConfiguration')}
              </Button>
              <Button 
                startIcon={<RestoreIcon />}
                variant="outlined"
                onClick={handleResetToEnv}
                disabled={resetToEnvMutation.isPending}
              >
                {t('embedding.actions.resetToEnvironment')}
              </Button>
            </Box>
          </Box>
          
          <List>
            {configs.map((config, index) => (
              <React.Fragment key={config.id || index}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {config.display_name || `${config.provider} Configuration`}
                        {!config.id && (
                          <Chip 
                            label={t('embedding.currentConfig.default')} 
                            color="primary" 
                            size="small" 
                            icon={<StarIcon />} 
                          />
                        )}
                        {config.is_active && (
                          <Chip label={t('embedding.currentConfig.active')} color="success" size="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Box component="span">
                          {t('embedding.currentConfig.provider')}: {config.provider} | {t('embedding.currentConfig.model')}: {config.model} | 
                          {t('embedding.currentConfig.vectorSize')}: {config.vector_size}
                        </Box>
                        {config.description && (
                          <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
                            {config.description}
                          </Box>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleTestConfig(config)}
                      title={t('embedding.actions.testConfiguration')}
                    >
                      <TestIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        if (!config.is_active) {
                          if (!config.id) {
                            // Environment default - use reset to env
                            handleResetToEnv();
                          } else {
                            // Regular config - use activate dialog
                            setConfigToActivate(config);
                            setActivateDialogOpen(true);
                          }
                        }
                      }}
                      title={config.is_active ? t('embedding.dialog.currentlyActive') : t('embedding.dialog.setAsActive')}
                      color={config.is_active ? "warning" : "default"}
                      disabled={config.is_active}
                    >
                      {config.is_active ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                    {config.id && (
                      <IconButton
                        onClick={() => openEditDialog(config)}
                        title={t('embedding.actions.editConfiguration')}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {config.id && (
                      <IconButton
                        onClick={() => {
                          setConfigToDelete(config.id!);
                          setDeleteDialogOpen(true);
                        }}
                        title={t('embedding.actions.deleteConfiguration')}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
                {index < configs.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </>
      ),
    },
    {
      label: t('embedding.tabs.availableProviders'),
      icon: <CloudIcon />,
      content: (
        <>
          <Typography variant="h6" gutterBottom>{t('embedding.providers.title')}</Typography>
          <Grid container spacing={2}>
            {providers.map((provider) => (
              <Grid size={{ xs: 12, md: 6 }} key={provider.value}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6">{provider.label}</Typography>
                      <Chip
                        label={provider.is_active ? t('embedding.currentConfig.active') : t('embedding.currentConfig.inactive')}
                        color={provider.is_active ? "success" : "default"}
                        icon={provider.is_active ? <CheckIcon /> : <WarningIcon />}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {provider.description}
                    </Typography>
                    <Typography variant="body2">
                      {t('embedding.providers.defaultModel')}: {provider.default_model}
                    </Typography>
                    <Typography variant="body2">
                      {t('embedding.providers.defaultVectorSize')}: {provider.default_vector_size}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('embedding.providers.requiredConfig')}: {provider.required_config.join(', ')}
                    </Typography>
                    {provider.optional_config.length > 0 && (
                      <Typography variant="body2">
                        {t('embedding.providers.optionalConfig')}: {provider.optional_config.join(', ')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      ),
    },
  ];

  return (
    <>
      <PageLayout
        title={t('embedding.title')}
        isLoading={configsLoading || currentConfigLoading || providersLoading}
        maxWidth="xl"
      >
        <AdminTabs
          tabs={tabs}
          value={tabValue}
          onChange={setTabValue}
          variant="standard"
        />
      </PageLayout>

      {/* Configuration Edit Dialog */}
      <EmbeddingConfigEditDialog
        open={configDialogOpen}
        config={editingConfig}
        providers={providers}
        onClose={() => setConfigDialogOpen(false)}
        onSave={handleSaveConfig}
        onTest={handleTestConfig}
      />

      {/* Test Result Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('embedding.dialog.testResultTitle')}</DialogTitle>
        <DialogContent>
          {testResult && (
            <Box>
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {testResult.message}
              </Alert>
              {testResult.provider_info && (
                <Box>
                  <Typography variant="h6" gutterBottom>{t('embedding.testResult.providerInformation')}</Typography>
                  <pre>{JSON.stringify(testResult.provider_info, null, 2)}</pre>
                </Box>
              )}
              {testResult.error && (
                <Box>
                  <Typography variant="h6" gutterBottom>{t('embedding.testResult.errorDetails')}</Typography>
                  <Typography variant="body2" color="error">
                    {testResult.error}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>{t('embedding.dialog.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('embedding.dialog.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('embedding.dialog.deleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('embedding.dialog.cancel')}</Button>
          <Button
            onClick={() => configToDelete && handleDeleteConfig(configToDelete)}
            color="error"
          >
            {t('embedding.dialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Dialog */}
      <Dialog 
        open={activateDialogOpen} 
        onClose={() => !activateConfigMutation.isPending && setActivateDialogOpen(false)} 
        maxWidth="md"
      >
        <DialogTitle>⚠️ {t('embedding.dialog.activateTitle')}</DialogTitle>
        <DialogContent>
          {configToActivate && (
            <Box>
              <Typography gutterBottom>
                {t('embedding.dialog.activateWarning')}
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('embedding.currentConfig.provider')}:</strong> {configToActivate.provider}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('embedding.currentConfig.model')}:</strong> {configToActivate.model}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('embedding.currentConfig.vectorSize')}:</strong> {configToActivate.vector_size}
                </Typography>
              </Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('embedding.dialog.activateImportant')}</strong>
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {t('embedding.dialog.activateConsequences', { returnObjects: true }).map((consequence: string, index: number) => (
                    <li key={index}>{consequence}</li>
                  ))}
                </ul>
              </Alert>
              <Typography variant="body2">
                {t('embedding.dialog.activateConfirm')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setActivateDialogOpen(false)}
            disabled={activateConfigMutation.isPending}
          >
            {t('embedding.dialog.cancel')}
          </Button>
          <Button 
            onClick={() => configToActivate && handleActivateConfig(configToActivate.id!)} 
            color="warning"
            variant="contained"
            disabled={activateConfigMutation.isPending}
            startIcon={activateConfigMutation.isPending ? <CircularProgress size={20} /> : <StarIcon />}
          >
            {activateConfigMutation.isPending ? t('embedding.dialog.activating') : t('embedding.dialog.activateButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Spinner */}
      {loading && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999
        }}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
}